import { useState } from 'react';
import axios from 'axios';
import { useNotificationContext } from '../contexts/NotificationContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const useCascadeDelete = () => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const { showSuccess, showError } = useNotificationContext();

  const getAuthConfig = () => ({
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });

  // Analyser l'impact de suppression d'une salle
  const analyzeRoomDeletion = async (roomId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/rooms/${roomId}/delete-analysis`, getAuthConfig());
      setAnalysis(response.data);
      return response.data;
    } catch (error) {
      showError('Erreur lors de l\'analyse de suppression');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Analyser l'impact de suppression d'un département
  const analyzeDepartmentDeletion = async (departmentId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/departments/${departmentId}/delete-analysis`, getAuthConfig());
      setAnalysis(response.data);
      return response.data;
    } catch (error) {
      showError('Erreur lors de l\'analyse de suppression');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Supprimer un enseignant
  const deleteTeacher = async (teacherId) => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API_URL}/api/teachers/${teacherId}`, getAuthConfig());
      showSuccess(`Enseignant supprimé avec ${response.data.sessionsDeleted} séances`);
      return response.data;
    } catch (error) {
      showError('Erreur lors de la suppression de l\'enseignant');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Supprimer un cours
  const deleteCourse = async (courseId) => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API_URL}/api/courses/${courseId}`, getAuthConfig());
      showSuccess(`Cours supprimé avec ${response.data.sessionsDeleted} séances`);
      return response.data;
    } catch (error) {
      showError('Erreur lors de la suppression du cours');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une classe
  const deleteClass = async (classId) => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API_URL}/api/classes/${classId}`, getAuthConfig());
      showSuccess(`Classe supprimée avec ${response.data.sessionsDeleted} séances`);
      return response.data;
    } catch (error) {
      showError('Erreur lors de la suppression de la classe');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une salle
  const deleteRoom = async (roomId, keepSessions = false) => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API_URL}/api/rooms/${roomId}`, {
        ...getAuthConfig(),
        data: { keepSessions }
      });
      
      const message = keepSessions 
        ? `Salle supprimée, ${response.data.sessionsAffected} séances conservées sans salle`
        : `Salle supprimée avec ${response.data.sessionsAffected} séances`;
      
      showSuccess(message);
      return response.data;
    } catch (error) {
      showError('Erreur lors de la suppression de la salle');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Supprimer un département
  const deleteDepartment = async (departmentId) => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API_URL}/api/departments/${departmentId}`, getAuthConfig());
      
      const { coursesDeleted, classesDeleted, teachersDeleted, tracksDeleted, sessionsDeleted } = response.data;
      const message = `Département supprimé avec ${coursesDeleted} cours, ${classesDeleted} classes, ${teachersDeleted} enseignants, ${tracksDeleted} filières et ${sessionsDeleted} séances`;
      
      showSuccess(message);
      return response.data;
    } catch (error) {
      showError('Erreur lors de la suppression du département');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    analysis,
    analyzeRoomDeletion,
    analyzeDepartmentDeletion,
    deleteTeacher,
    deleteCourse,
    deleteClass,
    deleteRoom,
    deleteDepartment
  };
};

export default useCascadeDelete;